import React, { useState, useRef } from 'react';
import { GenericFormModal } from './GenericFormModal';
import { NewsFormFields } from '../forms/NewsFormFields';
import { useAdmin } from '../context/AdminContext';
import { processFiles } from '../../../utils/heicConverter';

export function NewsFormModal({ isOpen, onClose, news }) {
  const { handleCreate, handleUpdate } = useAdmin();
  const [localFormData, setLocalFormData] = React.useState(news || {
    text_de: '',
    text_en: '',
    images: [],
    videos: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isProcessingFiles, setIsProcessingFiles] = useState(false);
  const formRef = useRef();

  // Update local form data when news prop changes
  React.useEffect(() => {
    if (news) {
      setLocalFormData({
        text_de: news.text_de || '',
        text_en: news.text_en || '',
        images: Array.isArray(news.images) ? news.images : [],
        videos: Array.isArray(news.videos) ? news.videos : []
      });
    } else {
      setLocalFormData({
        text_de: '',
        text_en: '',
        images: [],
        videos: []
      });
    }
  }, [news]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocalFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle file changes for multiple files with HEIC conversion
  const handleFileChange = async (e, field) => {
    const files = Array.from(e.target.files || []);
    
    if (files.length === 0) return;
    
    // Check if any HEIC files are present
    const heicFiles = files.filter(file => 
      file.name.toLowerCase().endsWith('.heic') || 
      file.name.toLowerCase().endsWith('.heif')
    );
    
    if (heicFiles.length > 0) {
      setIsProcessingFiles(true);
      console.log(`Processing ${heicFiles.length} HEIC file(s)...`);
    }
    
    try {
      // Process files (convert HEIC to JPEG if needed)
      const processedFiles = await processFiles(files, 0.9); // 90% quality
      
      console.log('Processed files:', processedFiles.map(f => ({
        name: f.name,
        type: f.type,
        size: f.size
      })));
      
      setLocalFormData(f => ({
        ...f,
        [field]: [...(Array.isArray(f[field]) ? f[field] : []), ...processedFiles]
      }));
      
      if (heicFiles.length > 0) {
        console.log(`Successfully converted ${heicFiles.length} HEIC file(s) to JPEG`);
      }
    } catch (error) {
      console.error('Error processing files:', error);
      // Fallback: use original files if conversion fails
      setLocalFormData(f => ({
        ...f,
        [field]: [...(Array.isArray(f[field]) ? f[field] : []), ...files]
      }));
    } finally {
      setIsProcessingFiles(false);
    }
  };

  // Remove image by index
  const handleRemoveImage = (idx) => {
    setLocalFormData(f => ({
      ...f,
      images: f.images.filter((_, i) => i !== idx)
    }));
  };

  // Remove video by index
  const handleRemoveVideo = (idx) => {
    setLocalFormData(f => ({
      ...f,
      videos: f.videos.filter((_, i) => i !== idx)
    }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setUploadProgress(0);
    
    try {
      // Use AdminContext which now handles news through apiClient
      if (news) {
        await handleUpdate('news', news.id, localFormData);
      } else {
        await handleCreate('news', localFormData);
      }

      // Reset form and close modal
      setLocalFormData({
        text_de: '',
        text_en: '',
        images: [],
        videos: []
      });
      if (formRef.current) {
        formRef.current.resetFileInputs();
      }
      onClose();
    } catch (err) {
      console.error('Error submitting news:', err);
      throw err; // Re-throw for modal error handling
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  return (
    <GenericFormModal
      isOpen={isOpen}
      onClose={onClose}
      title={news ? 'News bearbeiten' : 'News erstellen'}
      size="3xl"
    >
      <NewsFormFields
        ref={formRef}
        formData={localFormData}
        handleChange={handleChange}
        handleFileChange={handleFileChange}
        handleSubmit={handleSubmit}
        isEdit={!!news}
        onCancel={onClose}
        onRemoveImage={handleRemoveImage}
        onRemoveVideo={handleRemoveVideo}
        isSubmitting={isSubmitting}
        uploadProgress={uploadProgress}
        isProcessingFiles={isProcessingFiles}
      />
    </GenericFormModal>
  );
}